/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { canManageArtefacts } from '../permissions'
import ArtefactEditor from './ArtefactEditor'
import { exportArtefact } from './ExportPPT'

const ARTEFACT_TYPES = {
  kickoff_pack: {
    label: 'Bid Kick-off Pack',
    description: 'Opportunity overview, team structure, win themes, workstream owners and key dates for the bid kick-off meeting.',
    colour: '1A4035',
    bg: 'EEF7F2',
    icon: '🚀',
    export: 'pptx'
  },
  question_storyboard: {
    label: 'Question Storyboard',
    description: 'APMP-aligned response planning per ITT question. Win theme, discriminators, proof points, compliance matrix and graphics plan.',
    colour: '2D6A4F',
    bg: 'D4EAE0',
    icon: '📋',
    export: 'both'
  },
  blue_team: {
    label: 'Blue Team Review',
    description: 'Win theme testing, solution challenge and evaluator perspective exercise.',
    colour: '2B6CB0',
    bg: 'EBF8FF',
    icon: '🔵',
    export: 'pptx'
  },
  black_hat: {
    label: 'Black Hat Review',
    description: 'Competitive threat analysis from each competitor\'s perspective, with ghost strategy responses.',
    colour: '1C1C1C',
    bg: 'F7FAFC',
    icon: '🎩',
    export: 'pptx'
  },
  red_team: {
    label: 'Red Team Review',
    description: 'Formal quality review. Question by question scoring, compliance check, win theme consistency and improvement actions.',
    colour: 'C0392B',
    bg: 'FFF5F5',
    icon: '🔴',
    export: 'pptx'
  },
  sign_off_pack: {
    label: 'Sign-Off Pack',
    description: 'Board or authority briefing before submission. Solution summary, commercial position, risks, legal considerations and authorisation.',
    colour: 'C9A84C',
    bg: 'FFF8E7',
    icon: '✅',
    export: 'both'
  }
}

const STATUS_CONFIG = {
  draft:       { label: 'Draft',       colour: '718096', bg: 'F7FAFC' },
  in_progress: { label: 'In Progress', colour: 'C9A84C', bg: 'FFF8E7' },
  complete:    { label: 'Complete',    colour: '2D6A4F', bg: 'EEF7F2' },
  approved:    { label: 'Approved',    colour: '276749', bg: 'F0FFF4' },
}

function formatDate(date) {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  })
}

function Artefacts({ session, profile, onNavigate, linkedOpportunity }) {
  const [artefacts, setArtefacts] = useState([])
  const [opportunities, setOpportunities] = useState([])
  const [selectedOpp, setSelectedOpp] = useState(linkedOpportunity || null)
  const [loading, setLoading] = useState(false)
  const [showTypeSelector, setShowTypeSelector] = useState(false)
  const [editingArtefact, setEditingArtefact] = useState(null)
  const [error, setError] = useState(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadOpportunities()
  }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedOpp) loadArtefacts(selectedOpp.id)
  }, [selectedOpp])

  const loadOpportunities = async () => {
    const { data } = await supabase
      .from('opportunities')
      .select('id, title, buyer, stage, deadline')
      .order('created_at', { ascending: false })
    setOpportunities(data || [])
    if (linkedOpportunity) {
      setSelectedOpp(linkedOpportunity)
    } else if (data && data.length > 0 && !selectedOpp) {
      setSelectedOpp(data[0])
    }
  }

  const loadArtefacts = async (oppId) => {
    setLoading(true)
    const { data } = await supabase
      .from('artefacts')
      .select('*')
      .eq('opportunity_id', oppId)
      .order('created_at', { ascending: false })
    setArtefacts(data || [])
    setLoading(false)
  }

  const handleCreateArtefact = async (type) => {
    if (!selectedOpp) return
    const typeConfig = ARTEFACT_TYPES[type]
    const { data, error } = await supabase
      .from('artefacts')
      .insert([{
        opportunity_id: selectedOpp.id,
        type,
        title: `${typeConfig.label} — ${selectedOpp.title}`,
        status: 'draft',
        content: {},
        created_by: session.user.id
      }])
      .select()
      .single()
    if (!error && data) {
      setArtefacts(prev => [data, ...prev])
      setShowTypeSelector(false)
      setEditingArtefact(data)
    }
    if (error) setError(error.message)
  }

  const handleDeleteArtefact = async (id) => {
    await supabase.from('artefacts').delete().eq('id', id)
    setArtefacts(prev => prev.filter(a => a.id !== id))
  }

  const handleStatusChange = async (id, newStatus) => {
    await supabase.from('artefacts').update({ status: newStatus }).eq('id', id)
    setArtefacts(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a))
  }

  const handleArtefactSaved = (updated) => {
    setArtefacts(prev => prev.map(a => a.id === updated.id ? updated : a))
    setEditingArtefact(null)
  }

  // If editing an artefact open the editor
  if (editingArtefact) {
    return (
      <ArtefactEditor
        artefact={editingArtefact}
        opportunity={selectedOpp}
        session={session}
        profile={profile}
        onSaved={handleArtefactSaved}
        onBack={() => setEditingArtefact(null)}
      />
    )
  }

  return (
    <div className="scorecard-page container">
      <div className="page-header">
        <p className="label">Bid Artefacts</p>
        <h1>Bid Artefacts</h1>
        <p>Structured bid documents linked to each opportunity — from Kick-off Pack through to Sign-Off Pack.</p>
      </div>

      {/* Opportunity selector */}
      <div style={{
        background: 'var(--white)', border: '1px solid var(--green-light)',
        padding: '20px 24px', marginBottom: '24px'
      }}>
        <div style={{
          fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: 'var(--green)', marginBottom: '8px'
        }}>
          Opportunity
        </div>
        <select
          value={selectedOpp?.id || ''}
          onChange={e => {
            const opp = opportunities.find(o => o.id === e.target.value)
            setSelectedOpp(opp || null)
          }}
          style={{
            width: '100%', padding: '10px 14px',
            border: '1px solid var(--silver)', fontSize: '0.9375rem',
            color: 'var(--charcoal)', borderRadius: 0, outline: 'none',
            background: 'var(--white)'
          }}
        >
          <option value="">Select an opportunity</option>
          {opportunities.map(opp => (
            <option key={opp.id} value={opp.id}>
              {opp.title} — {opp.buyer}
            </option>
          ))}
        </select>
      </div>

      {selectedOpp && (
        <>
          {/* Opportunity context strip */}
          <div style={{
            background: 'var(--green-dark)', padding: '14px 20px',
            marginBottom: '20px', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <div style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1.125rem', fontWeight: 600, color: 'var(--white)'
              }}>
                {selectedOpp.title}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--green-light)', marginTop: '2px' }}>
                {selectedOpp.buyer}
                {selectedOpp.deadline && ` · Deadline: ${formatDate(selectedOpp.deadline)}`}
              </div>
            </div>
            <button
              className="btn btn--primary"
              onClick={() => canManageArtefacts(profile) && setShowTypeSelector(true)}
          disabled={!canManageArtefacts(profile)}
          style={{ display: canManageArtefacts(profile) ? undefined : 'none' }}
            >
              + New Artefact
            </button>
          </div>

          {/* Artefact type selector */}
          {showTypeSelector && (
            <div style={{
              background: 'var(--white)', border: '1px solid var(--green-light)',
              padding: '24px', marginBottom: '24px'
            }}>
              <div style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1.25rem', fontWeight: 600,
                color: 'var(--green-dark)', marginBottom: '20px'
              }}>
                Select Artefact Type
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px'
              }}>
                {Object.entries(ARTEFACT_TYPES).map(([type, config]) => (
                  <button
                    key={type}
                    onClick={() => handleCreateArtefact(type)}
                    style={{
                      background: 'var(--white)',
                      border: `1px solid #${config.colour}40`,
                      padding: '16px', textAlign: 'left',
                      cursor: 'pointer', transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = `#${config.bg}`
                      e.currentTarget.style.borderColor = `#${config.colour}`
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'var(--white)'
                      e.currentTarget.style.borderColor = `#${config.colour}40`
                    }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>
                      {config.icon}
                    </div>
                    <div style={{
                      fontSize: '0.875rem', fontWeight: 600,
                      color: `#${config.colour}`, marginBottom: '6px'
                    }}>
                      {config.label}
                    </div>
                    <div style={{
                      fontSize: '0.75rem', color: 'var(--slate)',
                      lineHeight: 1.4
                    }}>
                      {config.description}
                    </div>
                  </button>
                ))}
              </div>
              <button
                className="btn btn--secondary"
                style={{ marginTop: '16px' }}
                onClick={() => setShowTypeSelector(false)}
              >
                Cancel
              </button>
            </div>
          )}

          {error && <div className="error-msg">{error}</div>}

          {loading && <p style={{ color: 'var(--slate)' }}>Loading artefacts...</p>}

          {!loading && artefacts.length === 0 && !showTypeSelector && (
            <div className="card">
              <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
                No artefacts yet for this opportunity. Click <strong>+ New Artefact</strong> to create one.
              </p>
            </div>
          )}

          {/* Artefact cards */}
          {artefacts.map(artefact => {
            const typeConfig = ARTEFACT_TYPES[artefact.type]
            const statusConfig = STATUS_CONFIG[artefact.status]
            const contentKeys = Object.keys(artefact.content || {}).length
            return (
              <div
                key={artefact.id}
                style={{
                  background: 'var(--white)',
                  border: '1px solid var(--green-light)',
                  marginBottom: '12px',
                  transition: 'box-shadow 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(45,106,79,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ padding: '20px 24px' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: '16px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        gap: '10px', marginBottom: '8px', flexWrap: 'wrap'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>{typeConfig?.icon}</span>
                        <span style={{
                          fontSize: '0.625rem', fontWeight: 700,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          color: `#${typeConfig?.colour}`,
                          background: `#${typeConfig?.bg}`,
                          padding: '3px 8px',
                          border: `1px solid #${typeConfig?.colour}30`
                        }}>
                          {typeConfig?.label}
                        </span>
                        <span style={{
                          fontSize: '0.625rem', fontWeight: 700,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          color: `#${statusConfig?.colour}`,
                          background: `#${statusConfig?.bg}`,
                          padding: '3px 8px'
                        }}>
                          {statusConfig?.label}
                        </span>
                      </div>
                      <div style={{
                        fontFamily: 'Cormorant Garamond, serif',
                        fontSize: '1.125rem', fontWeight: 600,
                        color: 'var(--green-dark)', marginBottom: '4px'
                      }}>
                        {artefact.title}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--slate)' }}>
                        Created {formatDate(artefact.created_at)}
                        {contentKeys > 0 && ` · ${contentKeys} field${contentKeys !== 1 ? 's' : ''} completed`}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <div style={{
                        fontSize: '0.625rem', fontWeight: 600,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        color: 'var(--green)', marginBottom: '6px'
                      }}>
                        Status
                      </div>
                      <select
                        value={artefact.status}
                        onChange={e => handleStatusChange(artefact.id, e.target.value)}
                        style={{
                          padding: '6px 10px',
                          border: `1px solid #${statusConfig?.colour}`,
                          fontSize: '0.8125rem', fontWeight: 600,
                          color: `#${statusConfig?.colour}`,
                          background: `#${statusConfig?.bg}`,
                          borderRadius: 0, outline: 'none', cursor: 'pointer'
                        }}
                      >
                        {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                          <option key={key} value={key}>{val.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Action bar */}
                <div style={{
                  borderTop: '1px solid var(--green-light)',
                  background: 'var(--green-pale)',
                  padding: '8px 16px',
                  display: 'flex', gap: '8px',
                  alignItems: 'center', flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => canManageArtefacts(profile) && setEditingArtefact(artefact)}
          disabled={!canManageArtefacts(profile)}
          style={{ opacity: canManageArtefacts(profile) ? 1 : 0.3, cursor: canManageArtefacts(profile) ? 'pointer' : 'default', background: 'var(--green)', border: 'none', color: 'var(--white)', padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Open &amp; Edit
                  </button>
                  <button
                    onClick={() => exportArtefact(artefact, selectedOpp, profile?.organisations || null)}
                    style={{
                      background: 'none', border: '1px solid var(--green)',
                      color: 'var(--green)', padding: '5px 14px',
                      fontSize: '0.75rem', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--green)'; e.currentTarget.style.color = 'var(--white)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--green)' }}
                  >
                    Export PPT
                  </button>
                  <button
                    onClick={() => canManageArtefacts(profile) && handleDeleteArtefact(artefact.id)}
          disabled={!canManageArtefacts(profile)}
          style={{ opacity: canManageArtefacts(profile) ? 1 : 0.3, cursor: canManageArtefacts(profile) ? 'pointer' : 'default', background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '5px 10px', fontSize: '0.75rem', marginLeft: 'auto' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )
          })}
        </>
      )}

      {!selectedOpp && !loading && opportunities.length === 0 && (
        <div className="card">
          <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
            No opportunities found. Add an opportunity in the Pipeline first.
          </p>
        </div>
      )}

      <div className="actions" style={{ marginTop: '8px' }}>
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>
          ← Dashboard
        </button>
      </div>
    </div>
  )
}

export default Artefacts